import React, { useEffect, useState } from 'react';

/**
 * AvatarWithFallback — robustes Kundenfoto.
 * Rendert ein <img> und fällt bei einem Ladefehler (Browser-Cache, Adblocker,
 * Netzwerk) NIEMALS auf das kaputte Browser-Icon zurück, sondern zeigt einen
 * eleganten Initialen-Kreis (dezentes Emerald). Vorher wird — falls das Bild
 * ein Thumbnail (/avatars/authentic/96|224/…) ist — einmalig das Vollbild
 * versucht, damit möglichst ein echtes Foto erscheint.
 */

// Namen/Alt → Initialen (max. 2 Buchstaben, alles vor Komma/Trenner)
const initialsOf = (raw = '') => {
  const clean = String(raw).split(/[,·—–|]/)[0].trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (!parts.length) return '★';
  const first = parts[0][0] || '';
  const second = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + second).toUpperCase();
};

// Dezente Emerald/Teal-Töne — deterministisch pro Person (gleiche Person = gleiche Farbe)
const TONES = [
  'bg-emerald-600 text-white',
  'bg-emerald-100 text-emerald-800',
  'bg-teal-700 text-white',
  'bg-emerald-800 text-emerald-50',
  'bg-teal-100 text-teal-900',
];
const toneFor = (key = '') => {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return TONES[h % TONES.length];
};

// Thumbnail-Pfad → Vollbild-Pfad (1. Fallback vor den Initialen)
const fullFromThumb = (src) => {
  const m = /^\/avatars\/authentic\/(?:96|224)\/(.+)\.webp$/.exec(src || '');
  return m ? `/avatars/authentic/${m[1]}.webp` : null;
};

export default function AvatarWithFallback({
  src,
  alt = '',
  name,
  className = '',
  initialsClassName = 'text-xs font-semibold',
  'data-testid': testId,
  ...imgProps
}) {
  const [cur, setCur] = useState(src);
  const [failed, setFailed] = useState(!src);
  const label = name || alt;

  useEffect(() => {
    setCur(src);
    setFailed(!src);
  }, [src]);

  const handleError = () => {
    const full = fullFromThumb(cur);
    if (full && full !== cur) {
      setCur(full);
      return;
    }
    setFailed(true);
  };

  if (failed) {
    return (
      <div
        className={`${className} inline-flex items-center justify-center select-none ${toneFor(label)}`}
        role="img"
        aria-label={alt || undefined}
        data-testid={testId}
      >
        <span className={`leading-none tracking-tight ${initialsClassName}`}>{initialsOf(label)}</span>
      </div>
    );
  }

  return (
    <img
      src={cur}
      alt={alt}
      className={className}
      data-testid={testId}
      onError={handleError}
      {...imgProps}
    />
  );
}
